import { ChevronRight, EllipsisHorizontal } from "@procore/core-icons";
import { colors } from "@procore/core-react";
import React, { useCallback, useMemo, useRef } from "react";

import type { ListNode, Node, Tree } from "components/objectTree/types";
import { useHidden } from "contexts/hidden";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { highlightText } from "utils/textHighlight";
import { getGroupStateForFilteredTree } from "utils/treeHelpers";

import { INDEX_SUFFIX_PATTERN, MOBILE_ROW_LABEL_TO_TOGGLE_GAP, MOBILE_ROW_TOGGLE_TO_CHEVRON_GAP } from "../constants";
import { invertGroupState, useIsolationVisibility } from "../hooks/useIsolationVisibility";
import { formatNodeDisplayName } from "../utils/formatting";
import { SwipeableRow } from "./SwipeableRow";
import { VisibilityToggleButton } from "./VisibilityToggleButton";

export type MobileSearchResultsProps = {
    tree: Tree<ModelTreeNodeData>;
    filteredObjectIds: Set<number>;
    searchTerm?: string;
    onSelectGroup: (path: string) => void;
};

type Breadcrumb = {
    first: string;
    last?: string;
    hasOmittedNodes: boolean;
};

function deriveBreadcrumb(path: string): Breadcrumb | null {
    const parts = path
        .split("/")
        .slice(0, -1)
        .map((s) => s.replace(INDEX_SUFFIX_PATTERN, "$1"));
    if (parts.length === 0) {
        return null;
    }
    return {
        first: parts[0],
        last: parts.length > 1 ? parts[parts.length - 1] : undefined,
        hasOmittedNodes: parts.length > 2,
    };
}

function BreadcrumbRow({ breadcrumb }: { breadcrumb: Breadcrumb }) {
    return (
        <span
            data-testid="search-result-breadcrumb"
            style={{
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "center",
                columnGap: 4,
                minWidth: 0,
                overflow: "hidden",
                fontSize: 12,
                fontWeight: 500,
                lineHeight: "16px",
                color: colors.gray40,
            }}
        >
            {/* First segment — capped at 120px */}
            <span
                data-testid="breadcrumb-first-node"
                style={{
                    flexShrink: 0,
                    maxWidth: 120,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {breadcrumb.first}
            </span>

            {breadcrumb.last && (
                <>
                    <ChevronRight
                        data-testid="breadcrumb-chevron"
                        aria-hidden="true"
                        style={{ width: 12, height: 12, flexShrink: 0, color: colors.gray70 }}
                    />
                    {breadcrumb.hasOmittedNodes && (
                        <>
                            <EllipsisHorizontal
                                data-testid="breadcrumb-ellipsis"
                                aria-hidden="true"
                                style={{ width: 12, height: 12, flexShrink: 0, color: colors.gray40 }}
                            />
                            <ChevronRight
                                data-testid="breadcrumb-chevron"
                                aria-hidden="true"
                                style={{ width: 12, height: 12, flexShrink: 0, color: colors.gray70 }}
                            />
                        </>
                    )}
                    {/* Last segment — fills available space */}
                    <span
                        data-testid="breadcrumb-last-node"
                        style={{
                            flex: "1 1 0",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {breadcrumb.last}
                    </span>
                </>
            )}
        </span>
    );
}

export function MobileSearchResults({
    tree,
    filteredObjectIds,
    searchTerm = "",
    onSelectGroup,
}: MobileSearchResultsProps) {
    const activeRowCloseRef = useRef<(() => void) | null>(null);

    const handleRowOpen = useCallback((closeFn: () => void) => {
        if (activeRowCloseRef.current !== closeFn) {
            activeRowCloseRef.current?.();
        }
        activeRowCloseRef.current = closeFn;
    }, []);

    const matches = useMemo(
        () =>
            Object.values(tree)
                .filter((n): n is Node<ModelTreeNodeData> => n.path !== "" && filteredObjectIds.has(n.data.object.id))
                .sort((a, b) => a.path.localeCompare(b.path)),
        [tree, filteredObjectIds],
    );

    return (
        <div style={{ height: "100%", overflowY: "auto" }}>
            {matches.map((node) => (
                <SearchResultRow
                    key={node.path}
                    node={node}
                    tree={tree}
                    filteredObjectIds={filteredObjectIds}
                    searchTerm={searchTerm}
                    onSelectGroup={onSelectGroup}
                    onOpen={handleRowOpen}
                />
            ))}
        </div>
    );
}

function SearchResultRow({
    node,
    tree,
    filteredObjectIds,
    searchTerm,
    onSelectGroup,
    onOpen,
}: {
    node: Node<ModelTreeNodeData>;
    tree: Tree<ModelTreeNodeData>;
    filteredObjectIds: Set<number>;
    searchTerm: string;
    onSelectGroup: (path: string) => void;
    onOpen: (close: () => void) => void;
}) {
    const hidden = useHidden();
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;

    const item = useMemo((): ListNode<ModelTreeNodeData> => ({ node, parent: null, level: 0 }), [node]);
    const hiddenState = useMemo(() => {
        if (isolationVisibleIds) {
            return invertGroupState(getGroupStateForFilteredTree(isolationVisibleIds, item, filteredObjectIds, tree));
        }
        return getGroupStateForFilteredTree(hidden.ids, item, filteredObjectIds, tree);
    }, [hidden.ids, isolationVisibleIds, item, tree, filteredObjectIds]);
    const isHidden =
        hiddenState === "all" || (!isolationVisibleIds && !node.isLeaf && hidden.ids.has(node.data.object.id));

    const breadcrumb = deriveBreadcrumb(node.path);
    const displayName = formatNodeDisplayName(item);
    const isDrillable = !node.isLeaf;
    const contentOpacity = isHidden ? 0.4 : 1;

    return (
        <SwipeableRow node={node} onOpen={onOpen} filteredObjectIds={filteredObjectIds}>
            <div
                data-testid="mobile-search-result-row"
                onClick={isDrillable ? () => onSelectGroup(node.path) : undefined}
                style={{
                    display: "flex",
                    alignItems: "center",
                    paddingTop: 12,
                    paddingBottom: 12,
                    paddingLeft: 16,
                    paddingRight: 16,
                    position: "relative",
                    userSelect: "none",
                    cursor: isDrillable ? "pointer" : "default",
                }}
            >
                <span
                    style={{
                        flex: 1,
                        minWidth: 0,
                        marginRight: MOBILE_ROW_LABEL_TO_TOGGLE_GAP,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        opacity: contentOpacity,
                    }}
                >
                    {breadcrumb && <BreadcrumbRow breadcrumb={breadcrumb} />}
                    <span
                        data-testid="search-result-title"
                        style={{
                            overflowWrap: "anywhere",
                            fontSize: 18,
                            fontWeight: 600,
                            color: "#171a1c",
                            letterSpacing: "-0.28px",
                            lineHeight: "22px",
                        }}
                    >
                        {searchTerm ? highlightText(displayName, searchTerm) : displayName}
                    </span>
                </span>
                <VisibilityToggleButton node={node} tree={tree} filteredObjectIds={filteredObjectIds} />
                {isDrillable && (
                    <ChevronRight
                        data-testid="row-chevron"
                        style={{
                            flexShrink: 0,
                            width: 20,
                            height: 20,
                            marginLeft: MOBILE_ROW_TOGGLE_TO_CHEVRON_GAP,
                            color: "#ACB5B9",
                        }}
                    />
                )}
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        bottom: 0,
                        left: 16,
                        right: 16,
                        height: 1,
                        backgroundColor: "rgba(172, 181, 185, 0.32)",
                    }}
                />
            </div>
        </SwipeableRow>
    );
}
